import React, { useEffect, useMemo, useState } from 'react';
import { Briefcase, RefreshCw, Search } from 'lucide-react';
import { freelanceOrdersAPI } from '../utils/api';

const sectionLabels = {
  received: 'Список полученных задач',
  active: 'Задачи в работе',
  viewed: 'Просмотренные',
  rejected: 'Отклоненные',
};

const statusLabels = {
  new: 'Новая',
  review: 'Просмотрена',
  interesting: 'В работу',
  responded: 'Ответ отправлен',
  won: 'Выиграна',
  lost: 'Проиграна',
  ignored: 'Отклонена',
};

const statusActions = [
  ['new', 'В полученные'],
  ['interesting', 'В работу'],
  ['review', 'Просмотрено'],
  ['ignored', 'Отклонить'],
];

const FreelanceOrders = () => {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({ sections: [], marketplaces: [], statuses: [] });
  const [section, setSection] = useState('received');
  const [marketplace, setMarketplace] = useState('');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const sectionCounts = useMemo(() => {
    return Object.fromEntries((stats.sections || []).map((item) => [item.section, item.count]));
  }, [stats.sections]);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const params = { limit: 300 };
      if (section) params.section = section;
      if (marketplace) params.marketplace = marketplace;
      if (query) params.q = query;
      const [ordersData, statsData] = await Promise.all([
        freelanceOrdersAPI.getAll(params),
        freelanceOrdersAPI.getStats(),
      ]);
      setOrders(ordersData);
      setStats(statsData);
    } catch (err) {
      setError(err.message || 'Не удалось загрузить фриланс-заказы');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [section, marketplace]);

  const updateStatus = async (id, status) => {
    await freelanceOrdersAPI.update(id, { status });
    loadData();
  };

  return (
    <div className="h-full flex flex-col">
      <div className="glass border-b border-white/20 px-6 py-4 flex-shrink-0">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent flex items-center gap-2">
              <Briefcase className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              Фриланс
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">Задания из бирж и публичных источников. Это отдельный слой CRM, не организации.</p>
          </div>
          <button onClick={loadData} className="glass-button px-4 py-2 rounded-glass flex items-center gap-2 text-gray-700 dark:text-gray-200">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Обновить
          </button>
        </div>
      </div>

      <div className="p-6 overflow-auto space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {Object.entries(sectionLabels).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setSection(key)}
              className={`text-left p-4 rounded-glass border transition ${
                section === key
                  ? 'bg-blue-600 text-white border-blue-500 shadow-glow-sm'
                  : 'glass-button text-gray-700 dark:text-gray-200 border-white/20'
              }`}
            >
              <div className="text-sm opacity-80">{label}</div>
              <div className="text-2xl font-bold mt-1">{sectionCounts[key] || 0}</div>
            </button>
          ))}
        </div>

        <div className="glass-card p-4 rounded-glass border border-white/20">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <label className="block">
              <span className="text-sm text-gray-600 dark:text-gray-400">Биржа</span>
              <select value={marketplace} onChange={(e) => setMarketplace(e.target.value)} className="w-full mt-1 p-3 rounded-glass bg-white/70 dark:bg-slate-900/70 border border-white/20">
                <option value="">Все источники</option>
                {(stats.marketplaces || []).map((item) => (
                  <option key={item.marketplace || item.marketplace_label} value={item.marketplace || ''}>
                    {item.marketplace_label} ({item.count})
                  </option>
                ))}
              </select>
            </label>
            <label className="block lg:col-span-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">Поиск</span>
              <div className="flex gap-2 mt-1">
                <input value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && loadData()} placeholder="название, клиент, описание, навыки" className="flex-1 p-3 rounded-glass bg-white/70 dark:bg-slate-900/70 border border-white/20" />
                <button onClick={loadData} className="glass-button px-4 rounded-glass text-gray-700 dark:text-gray-200">
                  <Search className="w-5 h-5" />
                </button>
              </div>
            </label>
          </div>
        </div>

        {error && <div className="p-4 rounded-glass bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-200">{error}</div>}

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {orders.map((order) => (
            <div key={order.id} className="glass-card rounded-glass border border-white/20 p-4 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">#{order.id} · {order.marketplace_label || order.marketplace || order.source}</div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{order.title}</h2>
                </div>
                <span className="px-2 py-1 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-950/70 dark:text-blue-200 text-xs whitespace-nowrap">
                  {statusLabels[order.status] || order.status}
                </span>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-4">{order.description || order.ai_summary || 'Описание не указано.'}</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                <div><span className="text-gray-500 dark:text-gray-400">Скор</span><div className="font-semibold">{order.fit_score || 0}</div></div>
                <div><span className="text-gray-500 dark:text-gray-400">Риск</span><div className="font-semibold">{order.risk_score || 0}</div></div>
                <div><span className="text-gray-500 dark:text-gray-400">Бюджет</span><div className="font-semibold">{order.budget_max ? `${order.budget_max} ${order.currency || ''}` : '—'}</div></div>
                <div><span className="text-gray-500 dark:text-gray-400">Формат</span><div className="font-semibold">{order.remote_type || order.city || '—'}</div></div>
              </div>
              {order.skills && <div className="text-sm text-gray-600 dark:text-gray-300">{order.skills}</div>}
              <div className="flex flex-wrap gap-2">
                {order.source_url && (
                  <a href={order.source_url} target="_blank" rel="noreferrer" className="glass-button px-3 py-2 rounded-glass text-sm text-blue-700 dark:text-blue-300">
                    Открыть источник
                  </a>
                )}
                {statusActions.map(([value, label]) => (
                  <button key={value} onClick={() => updateStatus(order.id, value)} className="glass-button px-3 py-2 rounded-glass text-sm text-gray-700 dark:text-gray-200">
                    {label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {!loading && orders.length === 0 && (
          <div className="glass-card rounded-glass border border-white/20 p-8 text-center text-gray-600 dark:text-gray-400">
            В этом разделе пока нет заданий.
          </div>
        )}
      </div>
    </div>
  );
};

export default FreelanceOrders;
